import React, { useContext, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  InputGroupAddon,
  InputGroup,
} from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import ButtonIcon from "../common/ButtonIcon";
import classNames from "classnames";
import NumberFormat from "react-number-format";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { PreloadState } from "../UI/preloading/PreloadState";
import { faUndo } from "@fortawesome/free-solid-svg-icons";
import { fetchLiveExchangeRate, findAllCurrencies } from "../../helpers/api/currency";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { useSelector } from "react-redux";
import { CurrencyContext } from "../../context/Context";
import { formatAmountForBackend, getCurrencySymbol } from "../../helpers/currencyConversion";
import moment from "moment";
import { fetchAllPages } from "../../helpers/api/pagination";

const BASE_CURRENCY_OPTION = "__BASE_CURRENCY__";

const isBolivaresCurrency = (currency) => {
  const name = (currency?.name || "").toLowerCase();
  const iso = (currency?.iso || "").toLowerCase();
  const symbol = (currency?.symbol || "").toLowerCase();

  return (
    name.includes("boliva") ||
    iso === "ves" ||
    iso === "bs" ||
    symbol === "bs"
  );
};

const resolveCurrencySymbol = (currency, fallbackCurrency) => {
  if (currency?.symbol) {
    return currency.symbol;
  }

  if (isBolivaresCurrency(currency)) {
    return "Bs";
  }

  if (currency?.iso) {
    return getCurrencySymbol(currency.iso);
  }

  return fallbackCurrency?.symbol || "$";
};

const CreateBankForm = ({ bank, handleSubmitData, condominiumId }) => {
  const {
    register,
    handleSubmit,
    errors,
    formState: { isSubmitting },
    setValue,
    control,
    getValues,
    trigger,
    watch,
  } = useForm({
    defaultValues: bank
      ? {
        name: bank.name || "",
        account_number: bank.account_number || "",
        owner_name: bank.owner_name || "",
        owner_dni: bank.owner_dni || "",
        currency_id:
          bank.currency_id || bank.currency?.id || BASE_CURRENCY_OPTION,
      }
      : {},
  });

  const history = useHistory();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { baseCurrency } = useContext(CurrencyContext);
  const [prefix, setPrefix] = useState("V");
  const [currencies, setCurrencies] = useState([]);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);
  const [hasAppliedDefaultCurrency, setHasAppliedDefaultCurrency] =
    useState(false);

  const currentCondominiumId = condominiumId || bank?.condominium_id || bank?.condominium?.id || selectedCondominium;
  const watchedCurrencyId = watch("currency_id") || "";

  const submitForm = async (values) => {
    try {
      const normalizedCurrencyId =
        values.currency_id === BASE_CURRENCY_OPTION ? null : values.currency_id;

      let normalizedOpeningBalance = values.opening_balance;
      const numericOpeningBalance = Number(values.opening_balance || 0);
      const normalizedBalanceBs = numericOpeningBalance
        ? formatAmountForBackend(numericOpeningBalance)
        : "0.000000";
      const selectedCurrencyIso = (selectedCurrencyData?.iso || "").toUpperCase();
      const baseCurrencyIso = (baseCurrency?.iso || "USD").toUpperCase();
      const requiresConversion =
        !bank &&
        numericOpeningBalance > 0 &&
        normalizedCurrencyId &&
        selectedCurrencyIso &&
        selectedCurrencyIso !== baseCurrencyIso;
      let rate;

      if (requiresConversion) {
        const exchangeRateResponse = await fetchLiveExchangeRate({
          condominium_id: currentCondominiumId,
          date: moment().format("YYYY-MM-DD"),
        });
        rate = Number(exchangeRateResponse?.value || 0);

        if (!(rate > 0)) {
          throw new Error("No se pudo obtener una tasa de cambio valida para hoy.");
        }

        normalizedOpeningBalance = formatAmountForBackend(
          numericOpeningBalance / rate
        );
      }

      await handleSubmitData({
        ...values,
        currency_id: normalizedCurrencyId,
        opening_balance: formatAmountForBackend(normalizedOpeningBalance),
        balance_bs: normalizedBalanceBs,
        ...(rate ? { rate } : {}),
      });
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (bank) {
      register("account_number");
      isMounted && setValue("account_number", bank.account_number);
      register("owner_dni");
      isMounted && setValue("owner_dni", bank.owner_dni);
      isMounted && setPrefix(bank.owner_dni?.charAt(0));
      register("email");
      isMounted && setValue("email", bank.email || "");
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, [bank]);

  /*   useEffect(() => {
    if (dni) clearErrors("owner_dni");
    else setError("owner_dni", { message: "El DNI es obligatorio" });
    // eslint-disable-next-line
  }, [dni]); */

  useEffect(() => {
    let isMounted = true;
    if (condominiumId) {
      register("account_number");
      if (bank) {
        isMounted && setValue("account_number", bank.account_number);
      }
      register("condominium");
      isMounted && setValue("condominium", condominiumId);
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, [condominiumId]);

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!currentCondominiumId) return;

      isMounted && setIsLoadingCurrencies(true);

      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: currentCondominiumId,
        }));

        if (isMounted) {
          setCurrencies(response?.data || []);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setIsLoadingCurrencies(false);
        }
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [currentCondominiumId]);

  useEffect(() => {
    setHasAppliedDefaultCurrency(false);
  }, [currentCondominiumId, bank?.id]);

  const preferredDefaultCurrency = useMemo(() => {
    const bolivaresCurrency = (currencies || []).find((item) =>
      isBolivaresCurrency(item)
    );

    return bolivaresCurrency || null;
  }, [currencies]);

  const defaultCurrencyId = useMemo(() => {
    if (bank) {
      return String(
        bank.currency_id || bank.currency?.id || BASE_CURRENCY_OPTION
      );
    }

    if (preferredDefaultCurrency?.id) {
      return String(preferredDefaultCurrency.id);
    }

    return "";
  }, [bank, preferredDefaultCurrency]);

  useEffect(() => {
    if (
      isLoadingCurrencies ||
      !defaultCurrencyId ||
      hasAppliedDefaultCurrency ||
      watchedCurrencyId
    ) {
      return;
    }

    setValue("currency_id", String(defaultCurrencyId), {
      shouldDirty: false,
      shouldValidate: true,
    });
    setHasAppliedDefaultCurrency(true);
  }, [
    defaultCurrencyId,
    hasAppliedDefaultCurrency,
    isLoadingCurrencies,
    setValue,
    watchedCurrencyId,
  ]);

  const selectedCurrencyData = useMemo(() => {
    if (watchedCurrencyId === BASE_CURRENCY_OPTION) {
      return {
        id: BASE_CURRENCY_OPTION,
        iso: baseCurrency?.iso || "USD",
        name: baseCurrency?.name || "Moneda base",
        symbol: baseCurrency?.symbol || "$",
      };
    }

    return (currencies || []).find(
      (item) => String(item.id) === String(watchedCurrencyId || "")
    );
  }, [baseCurrency, currencies, watchedCurrencyId]);

  const selectedCurrencySymbol = useMemo(
    () => resolveCurrencySymbol(selectedCurrencyData, baseCurrency),
    [baseCurrency, selectedCurrencyData]
  );

  const renderCurrencySelect = () => (
    <FormGroup>
      <Label for="currency_id">Tipo de moneda</Label>
      <Controller
        name="currency_id"
        control={control}
        rules={{
          required: "Debe seleccionar un tipo de moneda.",
        }}
        defaultValue={defaultCurrencyId}
        render={({ onChange, value }) => (
          <Input
            type="select"
            id="currency_id"
            name="currency_id"
            value={value || ""}
            disabled={isLoadingCurrencies}
            className={classNames({
              "border-danger": errors.currency_id?.message,
            })}
            onChange={({ target }) => {
              onChange(target.value);
            }}
          >
            <option value="">
              {isLoadingCurrencies
                ? "Cargando monedas..."
                : "Selecciona una moneda"}
            </option>
            <option value={BASE_CURRENCY_OPTION}>
              ({baseCurrency?.iso || "USD"}) {baseCurrency?.name || "Moneda base"}
            </option>
            {currencies.map((currency) => (
              <option key={currency.id} value={String(currency.id)}>
                ({currency.iso}) {currency.name}
              </option>
            ))}
          </Input>
        )}
      />
      <WizardError
        error={errors.currency_id?.message}
        className="fs--1 font-weight-normal d-block"
      />
    </FormGroup>
  );

  console.log(errors);

  return (
    <>
      <Card
        data-testid="form"
        tag={Form}
        className="bg-light"
        onSubmit={handleSubmit(submitForm)}
      >
        <PreloadState
          loading={isSubmitting}
          overlay={true}
          message={isSubmitting && "Guardando por favor espere..."}
        >
          <FalconCardHeader title="Detalles del banco" light={false} />
          <CardBody>
            <>
              <FormGroup>
                <Label for="name">Nombre del banco</Label>
                <Input
                  autoComplete="off"
                  name="name"
                  defaultValue={bank?.name || undefined}
                  className={classNames({
                    "border-danger": errors.name?.message,
                  })}
                  innerRef={register({
                    required: "Nombre del banco es requerido.",
                    pattern: {
                      value: /^[a-zA-Z0-9 %\u00C0-\u00FF.,:;¡!¿?()\-_"']+$/i,
                      message: "Solo se aceptan alfanúmericos.",
                    },
                  })}
                  id="title"
                  placeholder="Nombre del banco"
                />
                <WizardError
                  error={errors.name?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </FormGroup>
              {bank && renderCurrencySelect()}
              <Row form>
                <Col sm tag={FormGroup}>
                  <Label for="account_number">
                    Número de cuenta
                  </Label>
                  <Controller
                    name="account_number"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const email = getValues("email");
                        const hasValue = !!value;
                        const hasEmail = !!email;

                        if (!hasValue && !hasEmail) {
                          return "Debe ingresar número de cuenta o correo.";
                        }
                        if (hasValue) {
                          const digitsOnly = value.replace(/\s+/g, "");
                          if (digitsOnly.length < 10) {
                            return "El número de cuenta debe tener al menos 10 caracteres.";
                          }
                        }
                        if (errors.email?.message === "Debe ingresar número de cuenta o correo.") {
                          trigger("email");
                        }
                        return true;
                      },
                    }}
                    render={({ onChange, name, value }) => (
                      <NumberFormat
                        onValueChange={({ value }) => {
                          // value viene sin espacios
                          setValue("account_number", value);
                          onChange(`${value}`);
                        }}
                        format="#### #### #### #### #### #### ####"
                        className={`${errors?.account_number?.message ? "border-danger" : ""
                          } form-control`}
                        name="account_number"
                        placeholder="Número de cuenta "
                        defaultValue={bank?.account_number?.toString() || ""}
                      />
                    )}
                  />

                  <WizardError
                    error={errors.account_number?.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                </Col>
                <Col sm tag={FormGroup}>
                  <Label for="owner_name">
                    Nombre del titular de la cuenta
                  </Label>
                  <Input
                    autoComplete="off"
                    name="owner_name"
                    id="owner_name"
                    defaultValue={bank?.owner_name || undefined}
                    placeholder="Nombre del titular"
                    className={classNames({
                      "border-danger": errors.owner_name?.message,
                    })}
                    innerRef={register({
                      required: "Nombre del titular de la cuenta es requerido.",
                      pattern: {
                        value: /^[a-zA-Z0-9 %\u00C0-\u00FF.,:;¡!¿?()\-_"']+$/i,
                        message: "Solo se aceptan alfanúmericos.",
                      },
                    })}
                  />
                  <WizardError
                    error={errors.owner_name?.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                </Col>
              </Row>
              <Row form className="mt-3">
                <Col md={6} tag={FormGroup}>
                  <Label>Correo electrónico </Label>
                  <Input
                    autoComplete="off"
                    name="email"
                    type="email"
                    defaultValue={bank?.email || undefined}
                    placeholder="correo@ejemplo.com"
                    className={classNames({
                      "border-danger": errors.email?.message,
                    })}
                    innerRef={register({
                      validate: (value) => {
                        const accountNumber = getValues("account_number");
                        const hasValue = !!value;
                        const hasAccount = !!accountNumber;

                        if (!hasValue && !hasAccount) {
                          return "Debe ingresar número de cuenta o correo.";
                        }
                        if (hasValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                          return "Correo inválido";
                        }
                        if (errors.account_number?.message === "Debe ingresar número de cuenta o correo.") {
                          trigger("account_number");
                        }
                        return true;
                      },
                    })}
                  />
                  <WizardError
                    error={errors.email?.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                </Col>

                <Col md={6} tag={FormGroup}>
                  <Label>DNI (Cédula de identidad o RIF)</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <Input
                        type="select"
                        onChange={(e) => {
                          setPrefix(e.target.value);
                        }}
                      >
                        <option value="V">V</option>
                        <option value="E">E</option>
                        <option value="J">J</option>
                      </Input>
                    </InputGroupAddon>
                    <Controller
                      name="owner_dni"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: "El DNI es obligatorio",
                        },
                        minLength: {
                          value: 7,
                          message:
                            "El DNI debe contener al menos 7 caracteres.",
                        },
                      }}
                      render={({ onChange, name, value }) => (
                        <NumberFormat
                          defaultValue={bank?.owner_dni?.toString() || ""}
                          className={`${errors?.owner_dni?.message ? "border-danger" : ""
                            } form-control input-spin-none`}
                          onValueChange={({ value }) => {
                            setValue("owner_dni", `${prefix}${value}`);
                            if (!value) onChange(null);
                            else onChange(`${prefix}${value}`);
                          }}
                          thousandSeparator={true}
                          renderText={(value, props) => (
                            <div {...props}>{value}</div>
                          )}
                        />
                      )}
                    />
                  </InputGroup>
                  {errors.owner_dni && (
                    <WizardError
                      error={errors.owner_dni.message}
                      className="fs--1 font-weight-normal d-block"
                    />
                  )}
                </Col>
              </Row>
              {!bank && (
                <Row form className="mt-3">
                  <Col md={4}>
                    {renderCurrencySelect()}
                  </Col>
                  <Col md={4} tag={FormGroup}>
                    <Label>Saldo inicial</Label>
                    <Controller
                      name="opening_balance"
                      control={control}
                      render={({ onChange, value }) => (
                        <NumberFormat
                          key={`opening-balance-${watchedCurrencyId || "empty"}`}
                          value={value || ""}
                          thousandSeparator={true}
                          decimalScale={2}
                          fixedDecimalScale={true}
                          allowNegative={false}
                          className="form-control"
                          placeholder="0.00"
                          prefix={`${selectedCurrencySymbol} `}
                          onValueChange={({ value }) => {
                            setValue("opening_balance", value);
                            onChange(value);
                          }}
                        />
                      )}
                    />
                  </Col>
                  <Col md={4} tag={FormGroup}>
                    <Label>Descripción del saldo inicial</Label>
                    <Input
                      autoComplete="off"
                      name="description"
                      type="text"
                      placeholder="Ej: Saldo inicial al registrar banco"
                      innerRef={register}
                    />
                  </Col>
                </Row>
              )}
              <Row form>
                <Col className="d-flex justify-content-end align-items-center mt-3">
                  <ButtonIcon
                    color="falcon-default"
                    className="px-5 mr-2"
                    icon={faUndo}
                    onClick={() => history.push("/dashboard/banks")}
                    iconAlign="right"
                    transform="down-1 shrink-4"
                  >
                    Volver
                  </ButtonIcon>
                  <ButtonIcon
                    data-testid="submit-button"
                    color="primary"
                    className="px-5"
                    type="submit"
                    icon="save"
                    iconAlign="right"
                    /*     disabled={(Object.keys(errors).length > 0)} */
                    transform="down-1 shrink-4"
                  >
                    Guardar
                  </ButtonIcon>
                </Col>
              </Row>
            </>
          </CardBody>
        </PreloadState>
      </Card>
    </>
  );
};

export default CreateBankForm;
